import FormatCurrency from 'components/Common/FormatCurrency'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import CurrencyContext from 'contexts/currencyContext'
import useInterval from 'hooks/useInterval'
import { useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import { memo, useCallback, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import NavBarButtonBase from '../NavBarButton/NavBarButtonBase'
import NavBarUserAvatar from '../NavBar/NavBarUserAvatar'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import AccountMenuDrawer from '../AccountMenu/AccountMenuDrawer'
import ModalContext from 'contexts/ModalContext'
import { closeTierProgressionNotification } from 'loyalty/components/LoyaltyTierProgressionNotification'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { mediaHoverable } from 'lib/theme/mediaQueries'

const FLIPPING_ROW_HEIGHT = rem(16)
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'
const SUMMARY_ROTATION_INTERVAL_MS = 5000

type SummaryRowPosition = 'active' | 'above' | 'below'

function getPrefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false
  }

  return window.matchMedia(REDUCED_MOTION_QUERY).matches
}

function subscribeToReducedMotionPreference(
  mediaQueryList: MediaQueryList,
  listener: () => void,
): () => void {
  if (mediaQueryList.addEventListener) {
    mediaQueryList.addEventListener('change', listener)

    return () => mediaQueryList.removeEventListener('change', listener)
  }

  mediaQueryList.addListener(listener)

  return () => mediaQueryList.removeListener(listener)
}

const SummaryText = styled(BodyText)`
  display: block;
`

const FlippingContainer = styled.div`
  position: relative;
  overflow: hidden;
  height: ${FLIPPING_ROW_HEIGHT};
`

const FlippingRow = styled(BodyText)<{ $position: SummaryRowPosition }>`
  position: absolute;
  inset: 0;
  display: block;
  opacity: ${props => props.$position === 'active' ? 1 : 0};
  transform: ${props => {
    if (props.$position === 'above') {
      return 'translateY(-100%)'
    }

    if (props.$position === 'below') {
      return 'translateY(100%)'
    }

    return 'translateY(0)'
  }};
  transition: opacity 300ms ease, transform 300ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const Button = styled(NavBarButtonBase)`
  padding: ${rem(12)};
  padding-left: ${rem(8)};
  display: flex;
  gap: ${rem(8)};
  align-items: center;
  text-align: start;
  max-width: ${rem(144)};
  transition: background-color 0.2s ease;

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.default.seven};
    }
  }
`

const NameSection = styled(CSSBreakpoint)`
  overflow: hidden;
`

interface Props {
  className?: string
}

function NavBarUserButton(props: Props) {
  const intl = useIntl()
  const account = useAppSelector(state => state.auth.account)
  const currency = useContext(CurrencyContext)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const loyaltyAccount = useAppSelector(state => state.loyalty.account.data)
  const [loyaltyTier] = useCurrentLoyaltyTier()
  const credits = useAppSelector(state => state.auth.account.creditsByCurrency[currency])
  const loyaltyPoints = loyaltyAccount?.points ?? 0
  const creditBalance = credits?.balance ?? 0
  const hasPoints = loyaltyPoints > 0
  const hasCredits = creditBalance > 0
  const showRotatingBalances = hasPoints && hasCredits
  const showTierSummary = !hasPoints && !hasCredits && hasLoyaltyAccount && !!loyaltyTier
  const [activeSummaryIndex, setActiveSummaryIndex] = useState(0)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(getPrefersReducedMotion)

  const showModal = useContext(ModalContext)
  const openAccountDrawer = useCallback(() => {
    closeTierProgressionNotification()
    showModal(<AccountMenuDrawer />)
  }, [showModal])
  const rotateSummary = useCallback(() => {
    setActiveSummaryIndex(currentIndex => currentIndex === 0 ? 1 : 0)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined
    }

    const mediaQueryList = window.matchMedia(REDUCED_MOTION_QUERY)
    const updateMotionPreference = () => setPrefersReducedMotion(mediaQueryList.matches)

    updateMotionPreference()

    return subscribeToReducedMotionPreference(mediaQueryList, updateMotionPreference)
  }, [])

  useEffect(() => {
    if (!showRotatingBalances || prefersReducedMotion) {
      setActiveSummaryIndex(0)
    }
  }, [prefersReducedMotion, showRotatingBalances])

  useInterval(rotateSummary, SUMMARY_ROTATION_INTERVAL_MS, {
    disabled: !showRotatingBalances || prefersReducedMotion,
  })

  return <Button
    onClick={openAccountDrawer}
    className={props.className}
    data-testid="nav-account"
  >
    <NavBarUserAvatar />
    <NameSection min="desktop">
      <BodyText variant="medium" wrap="truncate" colour="neutral-one">
        {account.givenName || intl.formatMessage({
          id: 'yruRQ-7AtFBKNQa-80cD',
          defaultMessage: 'Account',
          description: 'Fallback display name in navigation when user given name is not available',
        })}
      </BodyText>
      {showRotatingBalances && (
        <FlippingContainer data-testid="nav-account-summary">
          <FlippingRow
            variant="small"
            wrap="truncate"
            colour="neutral-two"
            $position={activeSummaryIndex === 0 ? 'active' : 'above'}
            aria-hidden={activeSummaryIndex !== 0}
            data-testid="nav-account-points-summary"
          >
            <FormatLoyaltyPoints abbreviated points={loyaltyPoints} />
          </FlippingRow>
          <FlippingRow
            variant="small"
            wrap="truncate"
            colour="neutral-two"
            $position={activeSummaryIndex === 1 ? 'active' : 'below'}
            aria-hidden={activeSummaryIndex !== 1}
            data-testid="nav-account-credit-summary"
          >
            <FormatCurrency value={creditBalance} />
          </FlippingRow>
        </FlippingContainer>
      )}

      {!showRotatingBalances && hasPoints && (
        <SummaryText variant="small" wrap="truncate" colour="neutral-two">
          <FormatLoyaltyPoints abbreviated points={loyaltyPoints} />
        </SummaryText>
      )}

      {!showRotatingBalances && !hasPoints && hasCredits && (
        <SummaryText variant="small" wrap="truncate" colour="neutral-two">
          <FormatCurrency value={creditBalance} />
        </SummaryText>
      )}

      {showTierSummary && (
        <BodyText variant="small" format="uppercase" weight="bold" wrap="truncate" colour={`tier-${loyaltyTier}-dark`}>
          {getLoyaltyTierDisplayName(loyaltyTier)}
        </BodyText>
      )}
    </NameSection>
  </Button>
}

export default memo(NavBarUserButton)
